import { useMemo } from 'react';
import Card from './Card';
import { useAppSelector } from '../store/hooks';
import CurrencyDisplay from './AmountDisplay';
import TotalBalanceCardSkeleton from './skeletons/TotalBalanceCardSkeleton'; // Updated import path
import { formatCurrency, MOCK_CURRENCY_SYMBOL } from '../utils/formatters';
import { useDisplayPrivacy } from '../contexts/DisplayPrivacyContext';

// Mock data for sparkline - replace with actual data fetching/generation
const sparklineData = [5, 10, 5, 20, 8, 15];

function TotalBalanceCard() {
  const { accounts, status, error } = useAppSelector(state => state.accounts);
  const { randomizeAmounts } = useDisplayPrivacy();
  const isInitialLoading = (status === 'loading' || status === 'idle') && accounts.length === 0;

  const { totalBalance, savingsBalance, creditCardBalance } = useMemo(() => {
    const parentAccounts = accounts.filter(acc => acc.parentAccountId === null || acc.parentAccountId === undefined);

    // Calculate total for SAVINGS accounts
    let savingsTotal = parentAccounts
      .filter(acc => acc.type === 'SAVINGS')
      .reduce((sum, acc) => sum + (acc.balance || 0), 0);

    // Calculate total for CREDIT_CARD accounts
    let creditCardTotal = parentAccounts
      .filter(acc => acc.type === 'CREDIT_CARD')
      .reduce((sum, acc) => sum + (acc.balance || 0), 0);

    if (creditCardTotal > 0) {
      savingsTotal += creditCardTotal;
      creditCardTotal = 0;
    }

    const total = savingsTotal + creditCardTotal;

    return { totalBalance: total, savingsBalance: savingsTotal, creditCardBalance: creditCardTotal };
  }, [accounts]);

  const renderSparkline = () => (
    <svg width="60" height="24" viewBox="0 0 60 24" className="text-green-500">
      <defs>
        <linearGradient id="sparklineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{ stopColor: 'currentColor', stopOpacity: 0.4 }} />
          <stop offset="100%" style={{ stopColor: 'currentColor', stopOpacity: 0 }} />
        </linearGradient>
      </defs>
      <path
        d={`M 0 ${24 - (sparklineData[0] / 20) * 20} L ${sparklineData.map((d, i) => `${i * (60 / (sparklineData.length - 1))} ${24 - (d / 20) * 20}`).join(' L ')}`}
        fill="url(#sparklineGradient)"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  if (isInitialLoading) {
    return <TotalBalanceCardSkeleton />; // Use the skeleton component
  }

  if (status === 'failed') {
    return <div className="p-4 text-center text-error">{error || 'Failed to load balance.'}</div>;
  }

  return (
    <Card>
      <div className="p-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center text-muted-foreground font-semibold">
            <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center mr-2 pt-[2px] text-xs">
              {randomizeAmounts ? MOCK_CURRENCY_SYMBOL : '₹'}
            </span>
            Available Balance
          </div>
        </div>

        {/* Balance and Sparkline */}
        <div className="flex justify-between items-center mb-4 gap-2">
          {/* Use CurrencyDisplay for total balance */}
          <CurrencyDisplay
            amount={totalBalance}
            className="text-3xl font-bold text-foreground"
            showFraction={false}
            showOnlyNegative={true}
          />
          {renderSparkline()}
        </div>

        {/* Aggregated Balances */}
        <div className="flex flex-wrap flex-row gap-2">
          {/* Savings Aggregate */}
          <div className="inline-flex items-center gap-1 rounded-full border border-border/80 bg-input/70 px-3 py-1">
            <span className="text-primary text-xs font-semibold">BALANCE</span>
            <span className="text-foreground text-xs font-bold">
              {formatCurrency(savingsBalance, { compact: true, currency: 'INR', showAbsolute: true, randomize: randomizeAmounts })}
            </span>
          </div>
          {/* Credit Card Aggregate */}
          <div className="inline-flex items-center gap-1 rounded-full border border-border/80 bg-input/70 px-3 py-1">
            <span className="text-primary text-xs font-semibold">DUES</span>
            <span className="text-foreground text-xs font-bold">
              {formatCurrency(creditCardBalance, { compact: true, currency: 'INR', showAbsolute: true, randomize: randomizeAmounts })}
            </span>
          </div>
        </div>
      </div>
      {/* Bottom corner button placeholder */}

    </Card>
  );
}

export default TotalBalanceCard;
